/**
 * MochiBow — module principal (ES module, chargé en defer).
 * Un seul IntersectionObserver partagé, aucune bibliothèque.
 * Composants : révélation au défilement, interrupteur d'animations, pause des boucles,
 * compte à rebours, quiz cadeau, parallaxe 2D, liste d'envies, barre de livraison offerte,
 * étoiles sur le panier.
 */
import { StandardEvents } from '@shopify/events';

const html = document.documentElement;
const MOTION_KEY = 'mb-motion';
const WISH_KEY = 'mb-wishlist';

const reducedMotion = () =>
  html.dataset.mbMotion === 'reduce' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Observateur partagé ---------- */
const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const el = entry.target;
      if (el.hasAttribute('data-mb-reveal')) {
        if (entry.isIntersecting) {
          el.classList.add('mb-in');
          observer.unobserve(el);
        }
      } else if (el.classList.contains('mb-loop')) {
        el.classList.toggle('mb-paused', !entry.isIntersecting);
      }
    }
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
);

function observeAll(root = document) {
  root.querySelectorAll('[data-mb-reveal]:not(.mb-in)').forEach((el, i) => {
    if (el.parentElement?.hasAttribute('data-mb-stagger')) {
      el.style.setProperty('--mb-i', String(Array.from(el.parentElement.children).indexOf(el)));
    }
    observer.observe(el);
  });
  root.querySelectorAll('.mb-loop').forEach((el) => observer.observe(el));
}

document.addEventListener('visibilitychange', () => {
  html.classList.toggle('mb-hidden', document.hidden);
});

/* Le module a chargé : on active les apparitions au défilement (les éléments sont visibles par défaut). */
html.classList.add('mb-js');

/* Section re-rendue par l'éditeur de thème : on ré-observe. */
document.addEventListener('shopify:section:load', (e) => observeAll(e.target));
observeAll();

/* ---------- Interrupteur « Réduire les animations » ---------- */
class MbMotionToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.sync();
    this.button.addEventListener('click', () => {
      const reduce = html.dataset.mbMotion !== 'reduce';
      if (reduce) html.dataset.mbMotion = 'reduce';
      else delete html.dataset.mbMotion;
      try {
        localStorage.setItem(MOTION_KEY, reduce ? 'reduce' : '');
      } catch (_) {
        /* stockage indisponible : le choix vaut pour la page */
      }
      this.sync();
    });
  }

  sync() {
    this.button.setAttribute('aria-pressed', html.dataset.mbMotion === 'reduce' ? 'true' : 'false');
  }
}
customElements.define('mb-motion-toggle', MbMotionToggle);

/* ---------- Lien « Gérer les cookies » ---------- */
class MbCookiePreferences extends HTMLElement {
  connectedCallback() {
    this.querySelector('a')?.addEventListener('click', (event) => {
      const banner = window.privacyBanner;
      if (!banner || typeof banner.showPreferences !== 'function') return;
      event.preventDefault();
      banner.showPreferences();
    });
  }
}
customElements.define('mb-cookie-preferences', MbCookiePreferences);

/* ---------- Compte à rebours réel ---------- */
class MbCountdown extends HTMLElement {
  /* Deux phases : avant la date de début, on compte jusqu'au début (libellé « commence dans ») ;
     ensuite on compte jusqu'à la fin (libellé « fin de l'offre dans ») ; passée la fin, la section se masque. */
  connectedCallback() {
    const deadline = Date.parse(this.dataset.deadline || '');
    if (Number.isNaN(deadline)) return;
    const start = Date.parse(this.dataset.start || '');
    this.fields = {
      d: this.querySelector('[data-unit="d"]'),
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };
    this.label = this.querySelector('[data-countdown-label]');
    const tick = () => {
      const now = Date.now();
      const beforeStart = !Number.isNaN(start) && now < start;
      const target = beforeStart ? start : deadline;
      this.setPhase(beforeStart ? 'start' : 'end');
      const diff = target - now;
      if (diff <= 0) {
        if (beforeStart) {
          this.timer = window.setTimeout(tick, 1000);
          return;
        }
        this.expire();
        return;
      }
      const s = Math.floor(diff / 1000);
      this.set('d', Math.floor(s / 86400));
      this.set('h', Math.floor((s % 86400) / 3600));
      this.set('m', Math.floor((s % 3600) / 60));
      this.set('s', s % 60);
      this.timer = window.setTimeout(tick, document.hidden ? 30000 : 1000);
    };
    tick();
  }

  setPhase(phase) {
    if (this.phase === phase) return;
    this.phase = phase;
    const text = phase === 'start' ? this.dataset.labelStart : this.dataset.labelEnd;
    if (this.label && text) this.label.textContent = text;
    this.setAttribute('aria-label', text || '');
    this.closest('[data-mb-season]')?.setAttribute('data-mb-phase', phase);
  }

  set(unit, value) {
    const el = this.fields[unit];
    if (el) el.textContent = String(value).padStart(2, '0');
  }

  expire() {
    const section = this.closest('[data-mb-hide-when-past]');
    if (section) section.hidden = true;
    else this.hidden = true;
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }
}
customElements.define('mb-countdown', MbCountdown);

/* ---------- Quiz « Trouve le cadeau parfait » ---------- */
class MbQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.result = this.querySelector('[data-result]');
    this.link = this.querySelector('[data-result-link]');
    this.answers = {};
    this.index = 0;
    this.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      this.answers[input.name] = input.value;
      const next = this.querySelector(`[data-step="${this.index}"] [data-next]`);
      if (next) next.disabled = false;
    });
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      if (btn.hasAttribute('data-next')) this.go(this.index + 1);
      if (btn.hasAttribute('data-prev')) this.go(this.index - 1);
      if (btn.hasAttribute('data-restart')) this.restart();
    });
    this.show(0, false);
  }

  show(i, focus = true) {
    this.index = i;
    this.steps.forEach((step, n) => {
      step.classList.toggle('is-active', n === i);
    });
    this.result?.classList.remove('is-active');
    if (focus) {
      const heading = this.steps[i]?.querySelector('h3, legend');
      heading?.focus?.({ preventScroll: true });
    }
  }

  go(i) {
    if (i >= this.steps.length) {
      this.finish();
      return;
    }
    if (i < 0) return;
    this.show(i);
  }

  finish() {
    const { who, perso, budget } = this.answers;
    const base = who || this.dataset.fallback || '/collections/all';
    const params = new URLSearchParams();
    if (perso && perso !== 'any') params.set('filter.p.tag', `perso:${perso}`);
    if (budget) {
      const [min, max] = budget.split('-');
      if (min) params.set('filter.v.price.gte', min);
      if (max) params.set('filter.v.price.lte', max);
    }
    const url = params.toString() ? `${base}?${params}` : base;
    this.steps.forEach((s) => s.classList.remove('is-active'));
    if (this.link) this.link.href = url;
    if (this.result) {
      this.result.classList.add('is-active');
      this.result.querySelector('h3')?.focus();
    }
    this.dispatchEvent(new CustomEvent('mb:quiz:complete', { bubbles: true, detail: { url, answers: this.answers } }));
  }

  restart() {
    this.answers = {};
    this.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
    this.querySelectorAll('[data-next]').forEach((b) => (b.disabled = true));
    this.show(0, false);
  }
}
customElements.define('mb-quiz', MbQuiz);

/* ---------- Parallaxe 2D légère (ordinateur, pointeur fin, mouvement autorisé) ---------- */
class MbParallax extends HTMLElement {
  connectedCallback() {
    if (reducedMotion() || !window.matchMedia('(pointer: fine) and (min-width: 990px)').matches) return;
    this.layers = Array.from(this.querySelectorAll('[data-depth]'));
    if (!this.layers.length) return;
    this.mouse = { x: 0, y: 0 };
    this.raf = 0;
    this.onMove = (e) => {
      const rect = this.getBoundingClientRect();
      this.mouse.x = (e.clientX - rect.left) / rect.width - 0.5;
      this.mouse.y = (e.clientY - rect.top) / rect.height - 0.5;
      this.schedule();
    };
    this.onScroll = () => this.schedule();
    this.addEventListener('pointermove', this.onMove, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  schedule() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      const scroll = Math.min(window.scrollY, 600);
      for (const layer of this.layers) {
        const depth = parseFloat(layer.dataset.depth || '0');
        const x = this.mouse.x * depth * 40;
        const y = this.mouse.y * depth * 30 + scroll * depth * 0.25;
        layer.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.raf);
  }
}
customElements.define('mb-parallax', MbParallax);

/* ---------- Liste d'envies (localStorage, partageable) ---------- */
const wishlist = {
  read() {
    try {
      const raw = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
    } catch (_) {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (_) {
      /* mode privé : la liste vit le temps de la page */
    }
    document.dispatchEvent(new CustomEvent('mb:wishlist:change', { detail: { list } }));
  },
  has(handle) {
    return this.read().includes(handle);
  },
  toggle(handle) {
    const list = this.read();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1);
    else list.push(handle);
    this.write(list);
    return i < 0;
  },
};

class MbWishButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    if (!this.button || !this.handle) return;
    this.sync();
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const added = wishlist.toggle(this.handle);
      this.sync();
      if (added) {
        this.button.classList.add('mb-wish--beat');
        this.button.addEventListener('animationend', () => this.button.classList.remove('mb-wish--beat'), { once: true });
      }
      this.announce(added);
    });
    this.onChange = () => this.sync();
    document.addEventListener('mb:wishlist:change', this.onChange);
  }

  sync() {
    const on = wishlist.has(this.handle);
    this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.button.setAttribute('aria-label', on ? this.dataset.labelRemove : this.dataset.labelAdd);
  }

  announce(added) {
    const live = document.getElementById('mb-live');
    if (live) live.textContent = added ? this.dataset.msgAdded : this.dataset.msgRemoved;
  }

  disconnectedCallback() {
    document.removeEventListener('mb:wishlist:change', this.onChange);
  }
}
customElements.define('mb-wish-button', MbWishButton);

class MbWishCount extends HTMLElement {
  connectedCallback() {
    this.badge = this.querySelector('[data-count]');
    this.sync();
    this.onChange = () => this.sync();
    document.addEventListener('mb:wishlist:change', this.onChange);
  }

  sync() {
    const n = wishlist.read().length;
    if (!this.badge) return;
    this.badge.textContent = String(n);
    this.badge.hidden = n === 0;
  }

  disconnectedCallback() {
    document.removeEventListener('mb:wishlist:change', this.onChange);
  }
}
customElements.define('mb-wish-count', MbWishCount);

class MbWishlistPage extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.empty = this.querySelector('[data-empty]');
    this.shareBtn = this.querySelector('[data-share]');
    this.shareMsg = this.querySelector('[data-share-msg]');
    this.sharedNote = this.querySelector('[data-shared-note]');
    const shared = new URLSearchParams(location.search).get('liste');
    this.handles = shared ? shared.split(',').filter(Boolean).slice(0, 60) : wishlist.read();
    this.readOnly = Boolean(shared);
    if (this.sharedNote) this.sharedNote.hidden = !this.readOnly;
    this.shareBtn?.addEventListener('click', () => this.share());
    this.onChange = () => {
      if (!this.readOnly) {
        this.handles = wishlist.read();
        this.render();
      }
    };
    document.addEventListener('mb:wishlist:change', this.onChange);
    await this.render();
  }

  async render() {
    if (!this.grid) return;
    if (!this.handles.length) {
      this.grid.innerHTML = '';
      if (this.empty) this.empty.hidden = false;
      if (this.shareBtn) this.shareBtn.hidden = true;
      return;
    }
    if (this.empty) this.empty.hidden = true;
    if (this.shareBtn) this.shareBtn.hidden = this.readOnly;
    const root = this.dataset.root || '/';
    const items = await Promise.all(
      this.handles.map(async (handle) => {
        try {
          const res = await fetch(`${root}products/${handle}.js`);
          if (!res.ok) return null;
          return await res.json();
        } catch (_) {
          return null;
        }
      })
    );
    this.grid.innerHTML = items
      .filter(Boolean)
      .map((p) => this.card(p))
      .join('');
    this.grid.querySelectorAll('mb-wish-button').forEach((el) => el.connectedCallback?.());
  }

  card(p) {
    const img = p.featured_image ? `${p.featured_image}&width=600`.replace('?v=', '?v=') : '';
    const price = (p.price / 100).toLocaleString(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });
    const src = img ? `${p.featured_image.split('?')[0]}?width=600` : '';
    return `<article class="mb-wishlist__card">
      <a class="mb-wishlist__link" href="${this.dataset.root || '/'}products/${p.handle}">
        ${src ? `<img src="${src}" alt="" width="600" height="600" loading="lazy">` : '<div class="mb-wishlist__noimg"></div>'}
        <h3>${this.escape(p.title)}</h3>
        <p>${price}</p>
      </a>
      ${this.readOnly ? '' : `<mb-wish-button data-handle="${p.handle}" data-label-add="${this.dataset.labelAdd}" data-label-remove="${this.dataset.labelRemove}" data-msg-added="" data-msg-removed=""><button type="button" class="mb-wish" aria-pressed="true"><svg viewBox="0 0 24 24" aria-hidden="true" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19.56 4.62 12.18A4.5 4.5 0 1 1 12 7.38a4.5 4.5 0 1 1 7.38 4.8z"/></svg></button></mb-wish-button>`}
    </article>`;
  }

  escape(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }

  async share() {
    const url = `${location.origin}${location.pathname}?liste=${encodeURIComponent(this.handles.join(','))}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        if (this.shareMsg) this.shareMsg.hidden = false;
      }
    } catch (_) {
      if (this.shareMsg) {
        this.shareMsg.textContent = url;
        this.shareMsg.hidden = false;
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('mb:wishlist:change', this.onChange);
  }
}
customElements.define('mb-wishlist-page', MbWishlistPage);

/* ---------- Barre de progression vers la livraison offerte ---------- */
class MbShippingBar extends HTMLElement {
  connectedCallback() {
    // Seuil en devise de la boutique (centimes) : converti avec le taux Shopify si le panier est en $, £, A$…
    const base = parseInt(this.dataset.threshold || '0', 10);
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    this.threshold = this.dataset.currency === this.dataset.shopCurrency ? base : Math.round(base * rate);
    this.text = this.querySelector('[data-text]');
    this.update(parseInt(this.dataset.total || '0', 10));
    this.onCart = async () => {
      try {
        const res = await fetch(`${this.dataset.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.update(cart.items_subtotal_price ?? cart.total_price);
      } catch (_) {
        /* on garde l'affichage courant */
      }
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    document.addEventListener('cart-section:restored', this.onCart);
  }

  update(total) {
    if (!this.threshold) return;
    const ratio = Math.min(total / this.threshold, 1);
    this.style.setProperty('--mb-ship-ratio', ratio.toFixed(3));
    this.style.setProperty('--mb-ship-progress', `${(ratio * 100).toFixed(1)}%`);
    const reached = total >= this.threshold;
    this.classList.toggle('mb-ship--reached', reached);
    if (this.text) {
      if (reached) {
        this.text.textContent = this.dataset.textReached || '';
      } else {
        const remaining = this.formatMoney(this.threshold - total);
        this.text.textContent = (this.dataset.textRemaining || '[[amount]]').replace('[[amount]]', remaining);
      }
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    document.removeEventListener('cart-section:restored', this.onCart);
  }
}
customElements.define('mb-shipping-bar', MbShippingBar);

/* ---------- Étoiles sur l'icône panier à chaque ajout ---------- */
document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  if (reducedMotion()) return;
  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.remove('mb-cart-bump');
    void icon.offsetWidth;
    icon.classList.add('mb-cart-bump');
    icon.addEventListener('animationend', () => icon.classList.remove('mb-cart-bump'), { once: true });
  });
});


/* ---------- Événements analytiques (quiz, liste d'envies) ---------- */
function track(name, data) {
  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish(name, data || {});
  }
}
document.addEventListener('mb:quiz:complete', (e) => track('mochibow_quiz_complete', e.detail));
document.addEventListener('mb:wishlist:change', (e) => track('mochibow_wishlist_change', { count: e.detail.list.length }));
